import { Card, Col, Input, Pagination, Row, Segmented, Select, Space, Statistic, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { fetchCustomerIntelligence } from "../../../apis/call_intelligence.api";
import { ago, fmtD, FlagTag, flagReason, inr, mins, minsSince, orderStatus, sentimentTag } from "./shared";

const { Text } = Typography;

const CustomerList = ({ onOpen }: { onOpen: (userId: string) => void }) => {
  const [data, setData] = useState<any[]>([]);
  const [kpis, setKpis] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [count, setCount] = useState(0);
  const [tab, setTab] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [city, setCity] = useState<string>();
  const [activeEscalation, setActiveEscalation] = useState<string>();

  const load = (p = page, size = pageSize) => {
    setLoading(true);
    fetchCustomerIntelligence({
      page: p,
      page_size: size,
      tab,
      search: search || undefined,
      city,
      active_escalation: activeEscalation,
    }).then((res) => {
      setData(res?.results || []);
      setCount(res?.count || 0);
      if (res?.kpis) setKpis(res.kpis);
      setLoading(false);
    });
  };

  useEffect(() => {
    setPage(1);
    load(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, city, activeEscalation]);

  const columns: ColumnsType<any> = [
    {
      title: "Customer",
      key: "customer",
      width: 180,
      render: (r) => (
        <div>
          <Text strong>{r.name || "Unnamed customer"}</Text>
          <div>
            <Text type="secondary">{r.phone || "Phone not linked"}</Text>
          </div>
        </div>
      ),
    },
    { title: "City", dataIndex: "city", key: "city", width: 120 },
    {
      title: "Orders",
      key: "orders",
      width: 120,
      render: (r) =>
        r.order_count ? (
          <span>
            {r.order_count} <Text type="secondary">({inr(r.order_value)})</Text>
          </span>
        ) : (
          <Text type="secondary">0</Text>
        ),
    },
    {
      title: "Last order",
      key: "last_order",
      width: 170,
      render: (r) =>
        r.last_order ? (
          <div>
            {fmtD(r.last_order.placed_at)}
            <div>
              <Text type="secondary">
                {r.last_order.enq} · {orderStatus(r.last_order.status)}
              </Text>
            </div>
          </div>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Active escalations",
      key: "escalations",
      width: 150,
      render: (r) => (
        <div>
          {r.active_escalations ? (
            <span>
              <Text strong>{r.active_escalations}</Text>{" "}
              <Text type="secondary">oldest {mins(minsSince(r.oldest_active_escalation_at))}</Text>
            </span>
          ) : (
            <Text type="secondary">0</Text>
          )}
          {r.active_support > 0 && (
            <div>
              <Text type="secondary">+ {r.active_support} support</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Last contact",
      key: "last_contact",
      width: 130,
      render: (r) =>
        r.last_contact ? (
          <div>
            {r.last_contact.label}
            <div>
              <Text type="secondary">{ago(r.last_contact.at)}</Text>
            </div>
          </div>
        ) : null,
    },
    {
      title: "Call sentiment and overall call summary",
      key: "sentiment",
      width: 340,
      render: (r) => (
        <div>
          {sentimentTag(r.latest_call?.sentiment, r.latest_call?.severity)}
          {r.call_count > 1 && <Text type="secondary">{r.call_count} calls</Text>}
          {r.overall_summary && (
            <Typography.Paragraph ellipsis={{ rows: 3, tooltip: r.overall_summary }} style={{ margin: "4px 0 0" }}>
              {r.overall_summary}
            </Typography.Paragraph>
          )}
        </div>
      ),
    },
    {
      title: "Flag",
      key: "flag",
      width: 210,
      render: (r) =>
        r.flagged ? (
          <div>
            <FlagTag flagged sentiment={r.latest_conversation_sentiment} />
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {flagReason(r)}
              </Text>
            </div>
          </div>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  const kpi = (title: string, value: any, hint: string) => (
    <Col xs={12} md={6}>
      <Card size="small">
        <Statistic title={title} value={value ?? "-"} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {hint}
        </Text>
      </Card>
    </Col>
  );

  return (
    <div>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {kpi("Customers", kpis?.customers, "order, call or ticket in the last 60 days")}
        {kpi("Flagged now", kpis?.flagged, "negative conversation, no call update in 1 h")}
        {kpi("Negative last conversation", kpis?.negative_last_conversation, "frustrated or angry")}
        {kpi("With active escalation", kpis?.with_active_escalation, "Escalation request type, active stage")}
      </Row>

      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          value={tab}
          onChange={(v) => setTab(v as string)}
          options={[
            { value: "all", label: "All customers (newest order first)" },
            { value: "flagged", label: `Flagged${kpis ? ` (${kpis.flagged})` : ""}` },
          ]}
        />
        <Input.Search
          placeholder="Name, phone digits, ENQ or ticket ID"
          allowClear
          style={{ width: 300 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={() => {
            setPage(1);
            load(1, pageSize);
          }}
        />
        <Select
          placeholder="All cities"
          allowClear
          style={{ width: 150 }}
          value={city}
          onChange={setCity}
          options={[
            { value: "Bangalore", label: "Bangalore" },
            { value: "Hyderabad", label: "Hyderabad" },
            { value: "Other", label: "Other" },
          ]}
        />
        <Select
          placeholder="Any escalation status"
          allowClear
          style={{ width: 210 }}
          value={activeEscalation}
          onChange={setActiveEscalation}
          options={[{ value: "1", label: "Has active escalation" }]}
        />
      </Space>

      <Table
        rowKey="customer_user"
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={false}
        size="middle"
        scroll={{ x: 1380 }}
        onRow={(r) => ({ onClick: () => onOpen(r.customer_user), style: { cursor: "pointer" } })}
      />

      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={count}
          showSizeChanger
          showTotal={(t) => `${t} customers`}
          onChange={(p, size) => {
            setPage(p);
            setPageSize(size);
            load(p, size);
          }}
        />
      </div>
    </div>
  );
};

export default CustomerList;
